import { debounce } from "lodash";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BseDto,
  ReceivedBseBidDto,
  WinningProductGroupBidsDto,
  useEditNegotiationRoundMutation,
  useUpdateBasicSourcingEventMutation,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { getLastSavedLabel } from "../../../../../common/formatting/getLastSavedLabel";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import {
  removeBid as removeBidAction,
  selectAllBids,
  selectAllBidsInGroup,
  selectBid as selectBidAction,
} from "../../../../../common/redux/reducers/basicSourcingEventReducer";
import { ProductGroup } from "../../../../../common/types";
import { eventIsClosed } from "../../../eventIsClosed";
import { useNegotiation } from "../NegotiationContext";
import {
  addAllBidsInGroupToSelection,
  addAllBidsToSelection,
  addBidToSelection,
  findProductGroupBid,
  getSelectedOrWinningBids,
  removeBidFromSelection,
} from "./BidEvaluationUtils";

const DEBOUNCE_MS = 3000;
const MAX_WAIT_MS = 10000;

export const useSelectProductBids = (sourcingEvent: BseDto) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentRound } = useNegotiation();
  const displayer = useApiError();

  const [updateSourcingEvent, { isLoading: isUpdatingEvent }] = useUpdateBasicSourcingEventMutation();
  const [editNegotiationRound, { isLoading: isUpdatingRound }] = useEditNegotiationRoundMutation();

  const [selectedBids, setSelectedBids] = useState<WinningProductGroupBidsDto[]>(() =>
    getSelectedOrWinningBids(sourcingEvent, currentRound),
  );
  const isSaving = isUpdatingEvent || isUpdatingRound;
  const selectionLocked = eventIsClosed(sourcingEvent);

  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSavedLabel, setLastSavedLabel] = useState<string | null>(null);

  useEffect(() => {
    if (hasPendingChanges) return;
    setSelectedBids(getSelectedOrWinningBids(sourcingEvent, currentRound));
  }, [sourcingEvent, currentRound, hasPendingChanges]);

  const save = useCallback(
    async (selectedProductBids: WinningProductGroupBidsDto[]) => {
      if (currentRound?.id) {
        await editNegotiationRound({
          negotiationId: currentRound.id,
          editNegotiationRequest: { selectedProductBids },
        }).unwrap();
      } else {
        await updateSourcingEvent({
          eventId: sourcingEvent.id,
          editSourcingEventRequest: { selectedProductBids },
        }).unwrap();
      }
    },
    [currentRound?.id, sourcingEvent.id, editNegotiationRound, updateSourcingEvent],
  );

  const debouncedSave = useMemo(
    () =>
      debounce(
        (bids: WinningProductGroupBidsDto[]) => {
          save(bids)
            .then(() => {
              setHasPendingChanges(false);
              setLastSavedAt(new Date());
            })
            .catch((e) => {
              displayer.trigger(e);
            });
        },
        DEBOUNCE_MS,
        { maxWait: MAX_WAIT_MS },
      ),
    [save, displayer],
  );

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  useEffect(() => {
    if (!hasPendingChanges) return;
    debouncedSave(selectedBids);
  }, [selectedBids, hasPendingChanges, debouncedSave]);

  useEffect(() => {
    if (!lastSavedAt) {
      setLastSavedLabel(null);
      return;
    }
    const updateLabel = () => setLastSavedLabel(getLastSavedLabel({ lastSavedAt, t }));
    updateLabel();
    const interval = setInterval(updateLabel, 5000);
    return () => clearInterval(interval);
  }, [lastSavedAt, t]);

  const selectBid = useCallback(
    ({ bid, productId, group }: { bid: ReceivedBseBidDto; productId: string; group: ProductGroup }) => {
      if (selectionLocked) return;
      dispatch(selectBidAction({ bid, productId, group, negotiationRoundId: currentRound?.id }));
      setSelectedBids((prev) => addBidToSelection(prev, group.id, productId, bid.id));
      setHasPendingChanges(true);
    },
    [dispatch, currentRound?.id, selectionLocked],
  );

  const removeBid = useCallback(
    ({ bid, productId, group }: { bid: ReceivedBseBidDto; productId: string; group: ProductGroup }) => {
      if (selectionLocked) return;
      dispatch(removeBidAction({ bid, productId, group, negotiationRoundId: currentRound?.id }));
      setSelectedBids((prev) => removeBidFromSelection(prev, group.id, productId, bid.id));
      setHasPendingChanges(true);
    },
    [dispatch, currentRound?.id, selectionLocked],
  );

  const selectAllBidsInGroupFromSupplier = useCallback(
    ({ bid, group }: { bid: ReceivedBseBidDto; group: ProductGroup }) => {
      if (selectionLocked) return;
      const groupBid = findProductGroupBid({ groupId: group.id, bid });
      if (!groupBid) return;
      dispatch(selectAllBidsInGroup({ bid, groupBid, negotiationRoundId: currentRound?.id }));
      setSelectedBids((prev) => addAllBidsInGroupToSelection(prev, groupBid, bid.id));
      setHasPendingChanges(true);
    },
    [dispatch, currentRound?.id, selectionLocked],
  );

  const selectAllBidsFromSupplier = useCallback(
    (bid: ReceivedBseBidDto) => {
      if (selectionLocked) return;
      dispatch(selectAllBids({ bid, negotiationRoundId: currentRound?.id }));
      setSelectedBids((prev) => addAllBidsToSelection(prev, bid));
      setHasPendingChanges(true);
    },
    [dispatch, currentRound?.id, selectionLocked],
  );

  const saveNow = useCallback(() => {
    debouncedSave.cancel();
    save(selectedBids)
      .then(() => {
        setHasPendingChanges(false);
        setLastSavedAt(new Date());
      })
      .catch((e) => {
        displayer.trigger(e);
        throw e;
      });
  }, [debouncedSave, save, selectedBids, displayer]);

  return {
    selectBid,
    removeBid,
    selectAllBidsInGroupFromSupplier,
    selectAllBidsFromSupplier,
    selectionLocked,
    hasPendingChanges,
    lastSavedLabel,
    saveNow,
    isSaving,
  };
};

type BidSelectionContextType = ReturnType<typeof useSelectProductBids>;

const BidSelectionContext = createContext<BidSelectionContextType | undefined>(undefined);

export const BidSelectionProvider = BidSelectionContext.Provider;

export const useBidSelection = (): BidSelectionContextType => {
  const context = useContext(BidSelectionContext);
  if (context === undefined) {
    throw new Error("useBidSelection must be used within a BidSelectionProvider");
  }
  return context;
};
